import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import {MatSelect, MatSelectChange} from "@angular/material/select";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";

const MULTISELECT_AUTO_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => MultiSelectWithBulkComponent),
	multi: true,
};

interface SelectOption {
	id: string;
	name: string;
}

export interface AutoSelectSummary {
	found: number;
	total: number;
	notFoundIds: string[];
}

const PAGE_SIZE = 20;

@Component({
	selector: 'multi-select-with-bulk',
	templateUrl: './multi-select-with-bulk.component.html',
	styleUrls: ['./multi-select-with-bulk.component.scss'],
	providers: [MULTISELECT_AUTO_VALUE_ACCESSOR],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectWithBulkComponent implements OnChanges, OnDestroy, ControlValueAccessor {

	@Input() label: string | undefined;
	@Input() placeholder: string | undefined;
	@Input() options: SelectOption[] = [];
	@Input() disabled: boolean = false;
	@Input() extraClass: string = '';
	@Input() searchable: boolean = false;

	@Output() onSelectionChange = new EventEmitter<string[]>();

	@ViewChild('selectElement') selectElement: MatSelect | undefined;
	@ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement> | undefined;

	value: string[] = [];
	searchValue: string = '';
	renderedOptions: SelectOption[] = [];
	hiddenSelectedOptions: SelectOption[] = [];
	totalFilteredCount: number = 0;
	tooltipText: string = '';
	displayText: string = '';
	currentPage: number = 1;
	totalPages: number = 1;
	readonly pageSize = PAGE_SIZE;

	/** Set when the search contains comma-separated IDs */
	autoSelectSummary: AutoSelectSummary | null = null;

	/** True when the search input contains commas (multi-value mode) */
	isMultiValueMode: boolean = false;

	private isDropdownOpen: boolean = false;
	private readonly searchSubject = new Subject<string>();
	private readonly destroy$ = new Subject<void>();
	private filterCache = new Map<string, SelectOption[]>();
	private currentFilteredOptions: SelectOption[] = [];

	constructor(private readonly cdr: ChangeDetectorRef) {
		this.setupSearchDebounce();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['options']) {
			const newOptions = changes['options'].currentValue;
			if (Array.isArray(newOptions)) {
				this.filterCache.clear();
				this.currentPage = 1;
				this.applyFilter(this.searchValue);
				this.updateTooltip();
			}
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.searchSubject.complete();
		this.filterCache.clear();
	}

	private setupSearchDebounce(): void {
		this.searchSubject.pipe(
			debounceTime(150),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe(searchTerm => {
			this.currentPage = 1;
			if (this.isMultiValueMode) {
				this.applyAutoSelect(searchTerm);
			} else {
				this.autoSelectSummary = null;
				this.applyFilter(searchTerm);
			}
			this.cdr.markForCheck();
		});
	}

	/**
	 * Parses comma-separated IDs from the search term, auto-selects matching options,
	 * and updates the summary with found/not-found counts.
	 */
	private applyAutoSelect(searchTerm: string): void {
		const ids = searchTerm.split(',').map(s => s.trim()).filter(s => s.length > 0);
		if (ids.length === 0) {
			this.autoSelectSummary = null;
			this.applyFilter('');
			return;
		}

		const optionMap = new Map<string, SelectOption>();
		(this.options || []).forEach(opt => optionMap.set(opt.id.toUpperCase(), opt));

		const foundIds: string[] = [];
		const notFoundIds: string[] = [];

		ids.forEach(id => {
			const match = optionMap.get(id.toUpperCase());
			if (match) {
				foundIds.push(match.id);
			} else {
				notFoundIds.push(id);
			}
		});

		const currentSelection = new Set(this.value || []);
		foundIds.forEach(id => currentSelection.add(id));
		const newValue = Array.from(currentSelection);

		this.value = newValue;
		this.onChange(newValue);
		this.onSelectionChange.emit(newValue);
		this.updateTooltip();

		this.autoSelectSummary = {
			found: foundIds.length,
			total: ids.length,
			notFoundIds,
		};

		// Show all options with selected items first
		this.applyFilter('', true);
		this.updateHiddenSelectedOptions();
	}

	private applyFilter(searchTerm: string, sortSelectedFirst: boolean = false): void {
		const normalizedTerm = (searchTerm || '').trim().toUpperCase();

		let filteredOptions: SelectOption[];

		if (!normalizedTerm) {
			filteredOptions = this.options ? [...this.options] : [];
		} else {
			filteredOptions = (this.options || []).filter(option =>
				option.id?.toUpperCase().includes(normalizedTerm) ||
				option.name?.toUpperCase().includes(normalizedTerm)
			);
		}

		if (sortSelectedFirst) {
			const selectedSet = new Set(this.value || []);
			if (selectedSet.size > 0) {
				const selectedOptions = filteredOptions.filter(opt => selectedSet.has(opt.id));
				const nonSelectedOptions = filteredOptions.filter(opt => !selectedSet.has(opt.id));
				filteredOptions = [...selectedOptions, ...nonSelectedOptions];
			}
		}

		this.currentFilteredOptions = filteredOptions;
		this.totalFilteredCount = filteredOptions.length;
		this.totalPages = Math.max(1, Math.ceil(filteredOptions.length / PAGE_SIZE));

		if (this.currentPage > this.totalPages) {
			this.currentPage = this.totalPages;
		}

		this.updateRenderedOptions();
	}

	private updateRenderedOptions(): void {
		const startIndex = (this.currentPage - 1) * PAGE_SIZE;
		const endIndex = startIndex + PAGE_SIZE;

		this.renderedOptions = this.currentFilteredOptions.slice(startIndex, endIndex);

		const renderedIds = new Set(this.renderedOptions.map(opt => opt.id));
		const selectedSet = new Set(this.value || []);

		this.hiddenSelectedOptions = (this.options || []).filter(opt =>
			selectedSet.has(opt.id) && !renderedIds.has(opt.id)
		);
	}

	goToFirstPage(event: MouseEvent): void {
		event.stopPropagation();
		if (this.currentPage !== 1) {
			this.currentPage = 1;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
			this.cdr.markForCheck();
		}
	}

	goToPreviousPage(event: MouseEvent): void {
		event.stopPropagation();
		if (this.currentPage > 1) {
			this.currentPage--;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
			this.cdr.markForCheck();
		}
	}

	goToNextPage(event: MouseEvent): void {
		event.stopPropagation();
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
			this.cdr.markForCheck();
		}
	}

	goToLastPage(event: MouseEvent): void {
		event.stopPropagation();
		if (this.currentPage !== this.totalPages) {
			this.currentPage = this.totalPages;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
			this.cdr.markForCheck();
		}
	}

	private scrollPanelToTop(): void {
		setTimeout(() => {
			const panel = this.selectElement?.panel?.nativeElement as HTMLElement;
			if (panel) {
				panel.scrollTop = 0;
			}
		});
	}

	onSearchValueChange(event: string): void {
		this.searchValue = event || '';
		this.isMultiValueMode = this.searchValue.includes(',');
		this.searchSubject.next(this.searchValue);
	}

	clearSearch(): void {
		this.searchValue = '';
		this.isMultiValueMode = false;
		this.autoSelectSummary = null;
		this.searchSubject.next('');
	}

	onMatSelectionChange(event: MatSelectChange): void {
		const selectedValues = event.value as string[];
		this.value = selectedValues;
		this.onChange(selectedValues);
		this.onSelectionChange.emit(selectedValues);
		this.updateTooltip();
		this.updateHiddenSelectedOptions();
		this.cdr.markForCheck();
	}

	private updateHiddenSelectedOptions(): void {
		const renderedIds = new Set(this.renderedOptions.map(opt => opt.id));
		const selectedSet = new Set(this.value || []);

		this.hiddenSelectedOptions = (this.options || []).filter(opt =>
			selectedSet.has(opt.id) && !renderedIds.has(opt.id)
		);
	}

	onOpenedChange(isOpen: boolean): void {
		this.isDropdownOpen = isOpen;

		if (isOpen) {
			this.currentPage = 1;
			this.applyFilter(this.searchValue, true);
			this.scrollPanelToTop();
			setTimeout(() => {
				this.searchInput?.nativeElement?.focus();
			});
		} else {
			if (this.searchValue) {
				this.clearSearch();
			}
			this.autoSelectSummary = null;
			this.isMultiValueMode = false;
		}
	}

	private updateTooltip(): void {
		if (!this.value?.length || !this.options?.length) {
			this.tooltipText = '';
			this.displayText = '';
			return;
		}

		const selectedNames = this.value
			.map(id => this.options.find(opt => opt.id === id)?.name)
			.filter(Boolean);

		this.tooltipText = selectedNames.join(', ');

		if (selectedNames.length <= 2) {
			this.displayText = selectedNames.join(', ');
		} else {
			this.displayText = `${selectedNames.length} selected`;
		}
	}

	trackByOptionId(index: number, option: SelectOption): string {
		return option.id;
	}

	isSelected(optionId: string): boolean {
		return this.value?.includes(optionId) ?? false;
	}

	// ControlValueAccessor implementation
	onChange: (value: string[]) => void = () => {};
	onTouched: () => void = () => {};

	writeValue(value: string | string[]): void {
		if (value === null || value === undefined) {
			this.value = [];
		} else if (Array.isArray(value)) {
			this.value = value;
		} else {
			this.value = [value];
		}
		this.updateTooltip();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: string[]) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}
}
